import "dayjs/locale/de";
import "dayjs/locale/en-gb";
import "dayjs/locale/ru";

import de from "i18n-iso-countries/langs/de.json";
import en from "i18n-iso-countries/langs/en.json";
import ru from "i18n-iso-countries/langs/ru.json";
import * as countries from "i18n-iso-countries";

import React, {
    type ContextType,
    createContext,
    type FC,
    type PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isBetween from "dayjs/plugin/isBetween";
import weekOfYear from "dayjs/plugin/weekOfYear";

import { type Lang, getLocaleMessages, type ILocaleMessages } from "@domain/intl";
import { IntlProvider } from "react-intl";
import { auth } from "@domain/api/firebase/firebase";

countries.registerLocale(en);
countries.registerLocale(de);
countries.registerLocale(ru);

dayjs.extend(customParseFormat);
dayjs.extend(localeData); // for getting month names from current locale
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(weekOfYear);

export enum AppError {
    IndexedDB = 1,
    FirebaseConnectionError,
    ReactRendering,
}

export const AppSettingsContext = createContext({
    intl: null as ILocaleMessages | null,
    changeLang: (_v: Lang) => {},
    useClassicImage: false,
    changeImage: (_old: boolean) => {},
    appError: undefined as AppError | undefined,
    setAppError: (_error: AppError | undefined) => {},
});

const lsKey = "lang";
const imgKey = "useOldImage";

export const getLocalStorageLang = () => localStorage.getItem(lsKey) as Lang;

export const AppSettingsProvider: FC<PropsWithChildren> = (props) => {
    const [intlState, setIntlState] = useState<ILocaleMessages>(getLocaleMessages(getLocalStorageLang()));
    const [useClassicImage, setUseClassicImage] = useState<boolean>(Boolean(localStorage.getItem(imgKey)));
    const [appError, setAppError] = useState<AppError>(undefined);

    const changeLang = useCallback((v: Lang) => {
        const msg = getLocaleMessages(v);
        const { locale } = msg;
        dayjs.locale(locale);
        auth.languageCode = locale;
        setIntlState(msg);
        localStorage.setItem(lsKey, locale);
    }, []);

    const changeImage = useCallback((useOld: boolean) => {
        setUseClassicImage(useOld);
        if (useOld) {
            localStorage.setItem(imgKey, "1");
        } else {
            localStorage.removeItem(imgKey);
        }
    }, []);

    useEffect(() => {
        changeLang(getLocalStorageLang());
    }, [changeLang]);

    const setAppErrorWithLog = useCallback((v: AppError) => {
        setAppError(v);
    }, []);

    const settingsValue: ContextType<typeof AppSettingsContext> = useMemo(
        () => ({
            intl: intlState,
            changeLang,
            useClassicImage,
            changeImage,
            appError,
            setAppError: setAppErrorWithLog,
        }),
        [changeLang, intlState, useClassicImage, changeImage, appError, setAppErrorWithLog],
    );

    return (
        <IntlProvider {...intlState}>
            <AppSettingsContext value={settingsValue}>{props.children}</AppSettingsContext>
        </IntlProvider>
    );
};
